// ============================================================================
// OBT F2-B — Formen cevap akışı API katmanı (paket-yerel)
// ============================================================================
// Mevcut paylaşılan istemci (src/api/client.js) üzerinden çalışır. Yeni axios
// instance KURULMAZ. Kanonik `/api` prefix'i kullanılır; `/api/v1` fallback
// KURULMAZ (backend her ikisini de mount eder, uygulama /api tercih eder).
//
// Ortak `obtApi.js` DEĞİŞTİRİLMEZ (F2-A/F2-B çakışma önlemi); F2-B çağrıları
// burada tutulur. Aktör kimliği/rolü ASLA gönderilmez — backend JWT'den çözer.
//
// Backend sözleşmesi (doğrulanmış — backend/modules/obt/api.py, schemas.py):
//   GET   /obt/my-publications                          → MyPublicationOut[]
//   GET   /obt/publications/{id}/snapshot               → PublicationSnapshotOut
//   GET   /obt/publications/{id}/my-parks               → MyParkOut[]
//   GET   /obt/publications/{id}/my-responses           → ResponseSummary[]
//   POST  /obt/publications/{id}/responses              → {response_id,status,scope_source}
//   PATCH /obt/responses/{id}                           → {response_id,status}
//   POST  /obt/responses/{id}/submit                    → {response_id,status}
//   POST  /obt/responses/{id}/resubmit                  → {response_id,status,review_state}
//   POST  /obt/publications/{id}/suggested-parks        → SuggestedParkResult (multipart)
//
// NOT (backend sözleşme sınırı): Formen'in KENDİ cevabının kayıtlı cevap
// içeriğini (answers/option/photo_id) geri okuyan bir uç YOKTUR. `/review`
// yalnız inceleyene (BM/MY/Müdür) açıktır; `my-responses` yalnız SAYAÇ döner.
// Bu nedenle taslak/gönderilmiş cevabın önceki içeriği yeniden GÖSTERİLEMEZ;
// F2-B bunu dürüstçe ele alır (bkz. responseEnums/hook notları).
// ============================================================================

import apiClient from '@/api/client';

const OBT = '/obt';

/**
 * GET /parks — aktörün KAPSAMINDAKİ parklar (sunucu `_scope_parks_for_user` ile
 * güvenli daraltır; istemci tüm belediye parklarını çekip filtrelemez). Filtre
 * dışı park önerisi için aday liste buradan gelir; kampanya kapsamındaki parklar
 * (my-parks) çağıran tarafından hariç tutulur.
 * @returns {Promise<Array<{id, name, region_id}>>}
 */
export async function listMyResponsibleParks({ signal } = {}) {
  const res = await apiClient.get('/parks', { signal });
  return Array.isArray(res.data) ? res.data : [];
}

/** GET /obt/my-publications — aktöre yöneltilen yayınlar + kendi sayaçları. */
export async function listMyPublications({ signal } = {}) {
  const res = await apiClient.get(`${OBT}/my-publications`, { signal });
  return Array.isArray(res.data) ? res.data : [];
}

/**
 * POST /obt/publications/{id}/distribute — kademeli devir: dağıtım görevlisi
 * (delegate BM/MY) bilgi talebini alt Formen'lerine dağıtır. İdempotent.
 * @returns {Promise<{publication_id, added, added_parks, formen_ids}>}
 */
export async function distributePublication(publicationId, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/publications/${encodeURIComponent(publicationId)}/distribute`,
    {},
    { signal }
  );
  return res.data;
}

/** GET /obt/publications/{id}/snapshot — immutable yayın + soru seti. */
export async function getPublicationSnapshot(publicationId, { signal } = {}) {
  const res = await apiClient.get(
    `${OBT}/publications/${encodeURIComponent(publicationId)}/snapshot`,
    { signal }
  );
  return res.data; // PublicationSnapshotOut
}

/** GET /obt/publications/{id}/my-parks — yalnız aktörün asli sorumluluğundaki parklar. */
export async function listMyParks(publicationId, { signal } = {}) {
  const res = await apiClient.get(
    `${OBT}/publications/${encodeURIComponent(publicationId)}/my-parks`,
    { signal }
  );
  return Array.isArray(res.data) ? res.data : [];
}

/** GET /obt/publications/{id}/my-responses — aktörün bu yayındaki kendi cevap özetleri. */
export async function listMyResponses(publicationId, { signal } = {}) {
  const res = await apiClient.get(
    `${OBT}/publications/${encodeURIComponent(publicationId)}/my-responses`,
    { signal }
  );
  return Array.isArray(res.data) ? res.data : [];
}

/**
 * GET /obt/responses/{id}/my-detail — cevap SAHİBİNİN kendi cevabının ayrıntısı
 * (owner-safe). answers[] `answer_id` + option_values + photo_ids; media[]
 * (answer_id ilişkisi + güvenli üstveri); aktif revizyon bilgisi; version.
 * Cevap içeriğini forma geri yüklemenin TEK güvenilir kaynağıdır.
 */
export async function getMyResponseDetail(responseId, { signal } = {}) {
  const res = await apiClient.get(
    `${OBT}/responses/${encodeURIComponent(responseId)}/my-detail`,
    { signal }
  );
  return res.data; // OwnerResponseDetailOut
}

/**
 * POST /obt/publications/{id}/responses — park için yeni cevap taslağı.
 * `publicationParkId` VEYA `supplementalParkId`'den TAM BİRİ verilir.
 */
export async function createResponseDraft(
  publicationId,
  { publicationParkId, supplementalParkId },
  { signal } = {}
) {
  const body = {};
  if (publicationParkId) body.publication_park_id = publicationParkId;
  if (supplementalParkId) body.supplemental_park_id = supplementalParkId;
  const res = await apiClient.post(
    `${OBT}/publications/${encodeURIComponent(publicationId)}/responses`,
    body,
    { signal }
  );
  return res.data; // {response_id, status, scope_source}
}

/**
 * PATCH /obt/responses/{id} — taslak güncelle (cevap metni + answers upsert).
 * NOT: backend DraftSaveIn `expected_version` KABUL ETMEZ (extra="forbid");
 * eşzamanlılık sunucu-tarafı satır kilidi/`version` kolonuyla korunur → 409.
 * @param {{responseText?: string, answers?: Array}} payload
 */
export async function updateResponseDraft(
  responseId,
  { responseText, answers, expectedVersion },
  { signal } = {}
) {
  const body = {};
  if (responseText !== undefined) body.response_text = responseText;
  if (Array.isArray(answers)) body.answers = answers;
  if (typeof expectedVersion === 'number') body.expected_version = expectedVersion;
  const res = await apiClient.patch(
    `${OBT}/responses/${encodeURIComponent(responseId)}`,
    body,
    { signal }
  );
  return res.data; // {response_id, status}
}

/** POST /obt/responses/{id}/submit — ilk gönderim (draft → submitted). */
export async function submitResponse(responseId, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/responses/${encodeURIComponent(responseId)}/submit`,
    {},
    { signal }
  );
  return res.data; // {response_id, status}
}

/**
 * GET /obt/publications/{id}/my-response-detail — aktörün bu yayındaki KENDİ
 * park cevaplarının önizlemesi (soru→cevap, not, foto, durum). "Önizle" içindir.
 * @returns {Promise<{campaign_id, person, responses: Array}>}
 */
export async function fetchMyResponseDetail(publicationId, { signal } = {}) {
  const res = await apiClient.get(
    `${OBT}/publications/${encodeURIComponent(publicationId)}/my-response-detail`,
    { signal }
  );
  return res.data; // RespondentDetailOut
}

/**
 * POST /obt/publications/{id}/submit-all — Model A: aktörün bu yayındaki TÜM
 * taslak cevaplarını topluca gönder. Eksik/geçersiz taslaklar atlanır ve
 * gerekçesiyle raporlanır.
 * @returns {Promise<{submitted:number, submitted_ids:string[],
 *   failed:Array<{response_id,park_name,reason}>, total_drafts:number}>}
 */
export async function submitAllDrafts(publicationId, { signal } = {}) {
  const res = await apiClient.post(
    `${OBT}/publications/${encodeURIComponent(publicationId)}/submit-all`,
    {},
    { signal }
  );
  return res.data;
}

/**
 * POST /obt/responses/{id}/resubmit — revizyon sonrası yeniden gönderim.
 * `comment`: Formen'in İNCELEYENE mesajı (isteğe bağlı) — BM'nin revizyon
 * talebine yanıt; inceleme geçmişinde görünür.
 */
export async function resubmitResponse(responseId, comment, { signal } = {}) {
  const body = comment && comment.trim() ? { comment: comment.trim() } : {};
  const res = await apiClient.post(
    `${OBT}/responses/${encodeURIComponent(responseId)}/resubmit`,
    body,
    { signal }
  );
  return res.data; // {response_id, status, review_state}
}

/**
 * POST /obt/publications/{id}/suggested-parks — filtre dışı park önerisi.
 * Multipart: park_id + description + 1..N fotoğraf (hepsi zorunlu).
 * @param {File[]} files en az bir görsel
 */
export async function createSuggestedPark(
  publicationId,
  { parkId, description, files },
  { signal } = {}
) {
  const form = new FormData();
  form.append('park_id', parkId);
  form.append('description', description);
  (files || []).forEach((f) => form.append('files', f, f.name));
  const res = await apiClient.post(
    `${OBT}/publications/${encodeURIComponent(publicationId)}/suggested-parks`,
    form,
    {
      signal,
      // Content-Type'ı tarayıcı boundary ile ayarlasın (elle set etme).
      headers: { 'Content-Type': undefined },
    }
  );
  return res.data; // SuggestedParkResult {suggestion_id, status, ...}
}
